import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Landing } from "@/components/Landing";
import { AccountPage } from "@/components/AccountPage";
import { InvestmentsPage } from "@/components/economy/InvestmentsPage";
import { CompanyDetailPage } from "@/components/economy/CompanyDetailPage";
import { EconomyAdminPage } from "@/components/economy/EconomyAdminPage";
import { AboutPage } from "@/components/about/AboutPage";
import { GalleryAdminPage } from "@/components/about/GalleryAdminPage";
import { AccountsAdminPage } from "@/components/account/AccountsAdminPage";
import { PatchesAdminPage } from "@/components/account/PatchesAdminPage";
import { RulesPage } from "@/components/rules/RulesPage";
import { LorePage } from "@/components/lore/LorePage";
import { JakHratPage } from "@/components/jak-hrat/JakHratPage";
import { Footer } from "@/components/Footer";
import { RegisterModal } from "@/components/RegisterModal";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/lib/auth-context";

function AppShell() {
  const [registerOpen, setRegisterOpen] = useState(false);
  const lenisRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const handleAnchor = (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const id = link.getAttribute("href");
      if (id === "#" || id === "#top") {
        e.preventDefault();
        lenis.scrollTo(0);
        return;
      }
      const target = document.querySelector(id);
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -80 });
      }
    };
    document.addEventListener("click", handleAnchor);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", handleAnchor);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const openRegister = () => setRegisterOpen(true);

  return (
    <div className="App relative">
      <Navbar onCreateAccount={openRegister} />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/ucet" element={<AccountPage />} />
          <Route path="/ekonomika" element={<InvestmentsPage />} />
          <Route path="/ekonomika/gm" element={<EconomyAdminPage />} />
          <Route path="/ekonomika/:companyId" element={<CompanyDetailPage />} />
          <Route path="/o-projektu" element={<AboutPage />} />
          <Route path="/pravidla" element={<RulesPage />} />
          <Route path="/lore" element={<LorePage />} />
          <Route path="/jak-hrat" element={<JakHratPage onCreateAccount={openRegister} />} />
          <Route path="/sprava/galerie" element={<GalleryAdminPage />} />
          <Route path="/sprava/ucty" element={<AccountsAdminPage />} />
          <Route path="/sprava/patche" element={<PatchesAdminPage />} />
        </Routes>
      </main>
      <Footer />
      <ScrollToTop />
      <RegisterModal open={registerOpen} onClose={() => setRegisterOpen(false)} />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#17171b",
            border: "1px solid rgba(240,128,31,0.35)",
            color: "#fff",
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
